import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { deleteDisbursementDraft } from "@/api/deleteDisbursementDraft";
import { getDisbursementDrafts } from "@/api/getDisbursementDrafts";
import { patchDisbursementStatus } from "@/api/patchDisbursementStatus";
import { postDisbursement, preparePostDisbursementData } from "@/api/postDisbursement";
import { postDisbursementFile } from "@/api/postDisbursementFile";
import { postDisbursementWithInstructions } from "@/api/postDisbursementWithInstructions";

import { endSessionIfTokenInvalid } from "@/helpers/endSessionIfTokenInvalid";
import { formatDisbursement } from "@/helpers/formatDisbursements";
import { normalizeApiError } from "@/helpers/normalizeApiError";
import { refreshSessionToken } from "@/helpers/refreshSessionToken";

import {
  ApiError,
  Disbursement,
  DisbursementDraft,
  DisbursementDraftRejectMessage,
  DisbursementDraftsInitialState,
  Pagination,
  RejectMessage,
} from "@/types";

import { RootState } from "@/store";

// `walletId` scopes the list to the account the user is currently on (X-Wallet-Id). The caller
// passes it from the SelectedWallet context; empty means "All accounts".
export const getDisbursementDraftsAction = createAsyncThunk<
  {
    items: DisbursementDraft[];
    pagination: Pagination;
  },
  { walletId: string },
  { rejectValue: RejectMessage; state: RootState }
>(
  "disbursementDrafts/getDisbursementDraftsAction",
  async ({ walletId }, { rejectWithValue, getState, dispatch }) => {
    const { token } = getState().userAccount;

    try {
      const { data, pagination } = await getDisbursementDrafts(token, walletId);
      refreshSessionToken(dispatch);

      return {
        items: data.map((d) => ({
          details: formatDisbursement(d),
          // TODO: get instructions once we have them in response
          instructions: {
            csvFile: undefined,
            csvName: undefined,
          },
        })),
        pagination,
      };
    } catch (error: unknown) {
      const errorString = normalizeApiError(error as ApiError).message;
      endSessionIfTokenInvalid(errorString, dispatch);

      return rejectWithValue({
        errorString: `Error fetching drafts: ${errorString}`,
      });
    }
  },
);

// What the wizard sends when it creates a disbursement: the create payload + CSV's identity
const draftFingerprint = (details: Disbursement, file?: File) =>
  JSON.stringify({
    ...preparePostDisbursementData(details),
    file: file ? [file.name, file.size, file.lastModified] : null,
  });

export const saveDisbursementDraftAction = createAsyncThunk<
  string,
  {
    details: Disbursement;
    file?: File;
    sourceWalletId?: string;
  },
  { rejectValue: DisbursementDraftRejectMessage; state: RootState }
>(
  "disbursementDrafts/saveDisbursementDraftAction",
  async ({ details, file, sourceWalletId }, { rejectWithValue, getState, dispatch }) => {
    const { token } = getState().userAccount;

    // Every save creates a new draft.
    // Saving under an existing name is rejected instead of overwriting.
    try {
      const newDisbursement = file
        ? await postDisbursementWithInstructions(token, details, file, sourceWalletId)
        : await postDisbursement(token, details, sourceWalletId);
      refreshSessionToken(dispatch);
      return newDisbursement.id;
    } catch (error: unknown) {
      const apiError = normalizeApiError(error as ApiError);
      const errorString = apiError.message;
      endSessionIfTokenInvalid(errorString, dispatch);

      return rejectWithValue({
        errorString: `Error saving draft: ${errorString}`,
        errorExtras: apiError?.extras,
      });
    }
  },
);

// `sourceWalletId` is the account the user picked in the create wizard; it funds the disbursement
// this thunk starts.
export const submitDisbursementNewDraftAction = createAsyncThunk<
  string,
  {
    details: Disbursement;
    file: File;
    sourceWalletId?: string;
  },
  { rejectValue: DisbursementDraftRejectMessage; state: RootState }
>(
  "disbursementDrafts/submitDisbursementNewDraftAction",
  async ({ details, file, sourceWalletId }, { rejectWithValue, getState, dispatch }) => {
    const { token } = getState().userAccount;
    const { newDraftId, newDraftWalletId, newDraftFingerprint } = getState().disbursementDrafts;
    const fingerprint = draftFingerprint(details, file);

    // A disbursement this visit created is started as-is while account and form still match.
    // Otherwise it is replaced.
    const isReusable =
      Boolean(newDraftId) &&
      newDraftWalletId === sourceWalletId &&
      newDraftFingerprint === fingerprint;

    let draftId = isReusable ? newDraftId : undefined;
    let remembered: Omit<DisbursementDraftRejectMessage, "errorString"> = {
      newDraftId,
      newDraftWalletId,
      newDraftFingerprint,
    };

    try {
      if (newDraftId && !isReusable) {
        await deleteDisbursementDraft(token, newDraftId);
        remembered = {};
      }

      if (!draftId) {
        const newDisbursement = await postDisbursementWithInstructions(
          token,
          details,
          file,
          sourceWalletId,
        );
        draftId = newDisbursement.id;
        remembered = {
          newDraftId: draftId,
          newDraftWalletId: sourceWalletId,
          newDraftFingerprint: fingerprint,
        };
      }

      await patchDisbursementStatus(token, draftId, "STARTED", sourceWalletId);
      refreshSessionToken(dispatch);

      return draftId;
    } catch (error: unknown) {
      const apiError = normalizeApiError(error as ApiError);
      const errorString = apiError.message;
      endSessionIfTokenInvalid(errorString, dispatch);

      return rejectWithValue({
        errorString: `Error submitting disbursement: ${errorString}`,
        errorExtras: apiError?.extras,
        ...remembered,
      });
    }
  },
);

export const saveNewCsvFileAction = createAsyncThunk<
  boolean,
  {
    savedDraftId: string;
    file: File;
  },
  { rejectValue: DisbursementDraftRejectMessage; state: RootState }
>(
  "disbursementDrafts/saveNewCsvFileAction",
  async ({ savedDraftId, file }, { rejectWithValue, getState, dispatch }) => {
    const { token } = getState().userAccount;
    try {
      await postDisbursementFile(token, savedDraftId, file);
      refreshSessionToken(dispatch);

      return true;
    } catch (error: unknown) {
      const apiError = normalizeApiError(error as ApiError);
      const errorString = apiError.message;
      endSessionIfTokenInvalid(errorString, dispatch);

      return rejectWithValue({
        errorString: `Error uploading new CSV file: ${errorString}`,
        errorExtras: apiError?.extras,
      });
    }
  },
);

// Acts on ONE saved draft, so it takes no ambient wallet id: the account is the draft's own
// `sourceWalletId`, read from the details slice that the draft page loaded.
export const submitDisbursementSavedDraftAction = createAsyncThunk<
  string,
  {
    // savedDraftId is always there for saved drafts, comes from the URL
    savedDraftId?: string;
    details: Disbursement;
    file: File;
  },
  { rejectValue: DisbursementDraftRejectMessage; state: RootState }
>(
  "disbursementDrafts/submitDisbursementSavedDraftAction",
  async ({ details, file, savedDraftId }, { rejectWithValue, getState, dispatch }) => {
    const { isApprovalRequired } = getState().organization.data;
    const { token } = getState().userAccount;
    const { id, sourceWalletId } = getState().disbursementDetails.details;
    const { newDraftId } = getState().disbursementDrafts;
    let draftId;

    try {
      draftId =
        savedDraftId ??
        // We might not need all of these ID checks, but I'll leave them here
        // for now just in case
        id ??
        newDraftId ??
        // Unreachable from the draft page (savedDraftId always comes from the URL). If it ever
        // is reached there is no chosen source account to honour, so the create goes out without
        // one — the backend rejects it on a multi-account tenant, which beats silently charging
        // whichever account the switcher happens to be on.
        (await postDisbursement(token, details)).id;

      if (!isApprovalRequired) {
        await postDisbursementFile(token, draftId, file);
      }

      // Send the account only when the details slice holds THIS draft, never another
      // disbursement's. The backend starts a disbursement from its stored account regardless.
      const draftWalletId = id === draftId ? sourceWalletId : undefined;
      await patchDisbursementStatus(token, draftId, "STARTED", draftWalletId);
      refreshSessionToken(dispatch);

      return draftId;
    } catch (error: unknown) {
      const apiError = normalizeApiError(error as ApiError);
      const errorString = apiError.message;
      endSessionIfTokenInvalid(errorString, dispatch);

      return rejectWithValue({
        errorString: `Error submitting disbursement: ${errorString}`,
        errorExtras: apiError?.extras,
        // Need to save draft ID if it failed because of CSV upload
        newDraftId: draftId,
      });
    }
  },
);

export const confirmDisbursementAction = createAsyncThunk<
  string,
  {
    savedDraftId?: string;
  },
  { rejectValue: DisbursementDraftRejectMessage; state: RootState }
>(
  "disbursementDrafts/confirmDisbursementAction",
  async ({ savedDraftId }, { rejectWithValue, getState, dispatch }) => {
    const { token } = getState().userAccount;
    const { id, sourceWalletId } = getState().disbursementDetails.details;
    const { newDraftId } = getState().disbursementDrafts;

    try {
      const draftId = savedDraftId ?? id ?? newDraftId;

      if (!draftId) {
        throw new Error("No draft ID available for confirmation");
      }

      // Status-only: nothing is created here. Account header as in
      // submitDisbursementSavedDraftAction.
      const draftWalletId = id === draftId ? sourceWalletId : undefined;
      await patchDisbursementStatus(token, draftId, "STARTED", draftWalletId);
      refreshSessionToken(dispatch);

      return draftId;
    } catch (error: unknown) {
      const apiError = normalizeApiError(error as ApiError);
      const errorString = apiError.message;
      endSessionIfTokenInvalid(errorString, dispatch);

      return rejectWithValue({
        errorString: `Error confirming disbursement: ${errorString}`,
        errorExtras: apiError?.extras,
      });
    }
  },
);

export const deleteDisbursementDraftAction = createAsyncThunk<
  void,
  string,
  { rejectValue: RejectMessage; state: RootState }
>(
  "disbursementDrafts/deleteDisbursementDraftAction",
  async (draftId, { rejectWithValue, getState, dispatch }) => {
    const { token } = getState().userAccount;

    try {
      await deleteDisbursementDraft(token, draftId);
      refreshSessionToken(dispatch);
      return;
    } catch (error: unknown) {
      const apiError = normalizeApiError(error as ApiError);
      const errorString = apiError.message;
      endSessionIfTokenInvalid(errorString, dispatch);

      return rejectWithValue({
        errorString: `Error deleting draft: ${errorString}`,
      });
    }
  },
);

const initialState: DisbursementDraftsInitialState = {
  items: [],
  status: undefined,
  newDraftId: undefined,
  newDraftWalletId: undefined,
  walletId: undefined,
  newDraftFingerprint: undefined,
  pagination: undefined,
  errorString: undefined,
  errorExtras: undefined,
  actionType: undefined,
  isCsvFileUpdated: undefined,
};

const disbursementDraftsSlice = createSlice({
  name: "disbursementDrafts",
  initialState,
  reducers: {
    resetDisbursementDraftsAction: () => initialState,
    clearDisbursementDraftsErrorAction: (state) => {
      state.errorString = undefined;
      state.errorExtras = undefined;
      state.status = "SUCCESS";
      state.actionType = undefined;
    },
    setDraftIdAction: (state, action: PayloadAction<string | undefined>) => {
      state.newDraftId = action.payload;
    },
    clearCsvUpdatedAction: (state) => {
      state.isCsvFileUpdated = false;
    },
  },
  extraReducers: (builder) => {
    // Get disbursement drafts
    builder.addCase(getDisbursementDraftsAction.pending, (state, action) => {
      state.status = "PENDING";
      state.walletId = action.meta.arg.walletId;
    });
    builder.addCase(getDisbursementDraftsAction.fulfilled, (state, action) => {
      // Drop results for an account the user has already switched away from.
      if (action.meta.arg.walletId !== state.walletId) {
        return;
      }

      state.items = action.payload.items;
      state.pagination = action.payload.pagination;
      state.status = "SUCCESS";
      state.newDraftId = undefined;
      state.errorString = undefined;
      state.errorExtras = undefined;
      state.actionType = undefined;
    });
    builder.addCase(getDisbursementDraftsAction.rejected, (state, action) => {
      if (action.meta.arg.walletId !== state.walletId) {
        return;
      }

      state.status = "ERROR";
      state.errorString = action.payload?.errorString;
    });
    // Save disbursement draft
    builder.addCase(saveDisbursementDraftAction.pending, (state = initialState) => {
      state.status = "PENDING";
      state.actionType = "save";
    });
    builder.addCase(saveDisbursementDraftAction.fulfilled, (state, action) => {
      state.newDraftId = action.payload;
      state.newDraftWalletId = action.meta.arg.sourceWalletId;
      state.newDraftFingerprint = draftFingerprint(action.meta.arg.details, action.meta.arg.file);
      state.status = "SUCCESS";
      state.errorString = undefined;
      state.errorExtras = undefined;
    });
    builder.addCase(saveDisbursementDraftAction.rejected, (state, action) => {
      state.status = "ERROR";
      state.errorString = action.payload?.errorString;
      state.errorExtras = action.payload?.errorExtras;
    });
    // Submit new disbursement
    builder.addCase(submitDisbursementNewDraftAction.pending, (state = initialState) => {
      state.status = "PENDING";
      state.actionType = "submit";
    });
    builder.addCase(submitDisbursementNewDraftAction.fulfilled, (state, action) => {
      state.newDraftId = action.payload;
      state.status = "SUCCESS";
      state.errorString = undefined;
    });
    builder.addCase(submitDisbursementNewDraftAction.rejected, (state, action) => {
      state.status = "ERROR";
      state.errorString = action.payload?.errorString;
      state.errorExtras = action.payload?.errorExtras;
      state.newDraftId = action.payload?.newDraftId;
      state.newDraftWalletId = action.payload?.newDraftWalletId;
      state.newDraftFingerprint = action.payload?.newDraftFingerprint;
    });
    // Submit new CSV file
    builder.addCase(saveNewCsvFileAction.pending, (state = initialState) => {
      state.status = "PENDING";
    });
    builder.addCase(saveNewCsvFileAction.fulfilled, (state, action) => {
      state.isCsvFileUpdated = action.payload;
      state.status = "SUCCESS";
      state.errorString = undefined;
    });
    builder.addCase(saveNewCsvFileAction.rejected, (state, action) => {
      state.status = "ERROR";
      state.errorString = action.payload?.errorString;
      state.errorExtras = action.payload?.errorExtras;
    });
    // Submit saved disbursement
    builder.addCase(submitDisbursementSavedDraftAction.pending, (state = initialState) => {
      state.status = "PENDING";
      state.actionType = "submit";
    });
    builder.addCase(submitDisbursementSavedDraftAction.fulfilled, (state, action) => {
      state.newDraftId = action.payload;
      state.status = "SUCCESS";
      state.errorString = undefined;
    });
    builder.addCase(submitDisbursementSavedDraftAction.rejected, (state, action) => {
      state.status = "ERROR";
      state.errorString = action.payload?.errorString;
      state.errorExtras = action.payload?.errorExtras;
      state.newDraftId = action.payload?.newDraftId;
    });
    // Confirm disbursement (status-only)
    builder.addCase(confirmDisbursementAction.pending, (state = initialState) => {
      state.status = "PENDING";
      state.actionType = "submit";
    });
    builder.addCase(confirmDisbursementAction.fulfilled, (state, action) => {
      state.newDraftId = action.payload;
      state.status = "SUCCESS";
      state.errorString = undefined;
    });
    builder.addCase(confirmDisbursementAction.rejected, (state, action) => {
      state.status = "ERROR";
      state.errorString = action.payload?.errorString;
      state.errorExtras = action.payload?.errorExtras;
    });
    // Delete disbursement draft
    builder.addCase(deleteDisbursementDraftAction.pending, (state) => {
      state.status = "PENDING";
      state.actionType = "delete";
    });
    builder.addCase(deleteDisbursementDraftAction.fulfilled, (state) => {
      state.status = "SUCCESS";
      state.actionType = "delete";
    });
    builder.addCase(deleteDisbursementDraftAction.rejected, (state, action) => {
      state.status = "ERROR";
      state.actionType = "delete";
      state.errorString = action.payload?.errorString;
    });
  },
});

export const disbursementDraftsSelector = (state: RootState) => state.disbursementDrafts;
export const { reducer } = disbursementDraftsSlice;
export const {
  resetDisbursementDraftsAction,
  clearDisbursementDraftsErrorAction,
  setDraftIdAction,
  clearCsvUpdatedAction,
} = disbursementDraftsSlice.actions;
